import { useCallback } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  BrandAccessStatusResponse,
  BrandRequestApiError,
  BrandAccessRequestPayload,
  BrandAccessRequestResponse,
  AvailableBrandsResponse,
  BrandAccessOnboardingStatusResponse,
  AvailableBrandsRequestParams,
} from './types'
import { useAuth } from '../../shared/auth'
import {
  getBrandAccessStatusApi,
  requestBrandAccessApi,
  getAvailableBrandsApi,
  getBrandAccessOnboardingStatusApi,
} from './helpers'

export function useFetchBrandAccessStatus() {
  const {
    mutate: fetchBrandAccessStatus,
    mutateAsync: fetchBrandAccessStatusAsync,
    data,
    isPending,
    error,
    isSuccess,
  } = useMutation<BrandAccessStatusResponse, BrandRequestApiError, void>({
    mutationFn: async () => {
      return getBrandAccessStatusApi()
    },
  })

  return {
    fetchBrandAccessStatus,
    fetchBrandAccessStatusAsync,
    data,
    isLoading: isPending,
    error,
    isSuccess,
  }
}

const BRAND_ACCESS_ONBOARDING_STATUS_QUERY_KEY = ['brandAccessOnboardingStatus'] as const

export function useFetchBrandAccessOnboardingStatus() {
  const { userId } = useAuth()

  const { data, isPending, error, isSuccess, refetch } = useQuery<
    BrandAccessOnboardingStatusResponse,
    BrandRequestApiError
  >({
    queryKey: BRAND_ACCESS_ONBOARDING_STATUS_QUERY_KEY,
    queryFn: getBrandAccessOnboardingStatusApi,
    staleTime: 2000,
    enabled: !!userId,
  })

  const fetchBrandAccessOnboardingStatusAsync =
    useCallback(async (): Promise<BrandAccessOnboardingStatusResponse> => {
      const result = await refetch()
      if (result.error) throw result.error
      if (result.data === undefined) throw new Error('No data')
      return result.data
    }, [refetch])

  return {
    fetchBrandAccessOnboardingStatus: () => refetch(),
    fetchBrandAccessOnboardingStatusAsync,
    data,
    isLoading: isPending,
    error: error ?? undefined,
    isSuccess,
  }
}

export function useRequestBrandAccess() {
  const {
    mutate: requestBrandAccess,
    mutateAsync: requestBrandAccessAsync,
    data,
    isPending,
    error,
    isSuccess,
  } = useMutation<BrandAccessRequestResponse, BrandRequestApiError, BrandAccessRequestPayload>({
    mutationFn: async (payload: BrandAccessRequestPayload) => {
      return requestBrandAccessApi(payload)
    },
  })

  return {
    requestBrandAccess,
    requestBrandAccessAsync,
    data,
    isLoading: isPending,
    error,
    isSuccess,
  }
}

export function useFetchAvailableBrands() {
  const {
    mutate: fetchAvailableBrands,
    mutateAsync: fetchAvailableBrandsAsync,
    data,
    isPending,
    error,
    isSuccess,
  } = useMutation<AvailableBrandsResponse, BrandRequestApiError, AvailableBrandsRequestParams>({
    mutationFn: async (params: AvailableBrandsRequestParams) => {
      return getAvailableBrandsApi(params)
    },
  })

  return {
    fetchAvailableBrands,
    fetchAvailableBrandsAsync,
    data,
    isLoading: isPending,
    error,
    isSuccess,
  }
}
